import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import { useAuth } from 'contexts/auth';

const QueryParamConfig = {
  askKlueVisibilityGroupId: withDefault(StringParam, undefined),
};

/**
 * Returns the visibility group ID the user is "viewing as" for the purposes
 * of AskKlue product behavior — used both to flag the BE call (so it returns
 * the consumer toolset for the previewed view) and to decide whether to show
 * the source-filter dropdown in the chat input.
 *
 * Two URL-driven mechanisms feed this:
 * - General view-as: `?previewing=<id>`, stored in `auth.visibilityGroup` by
 *   `useAuth`. Flips `isCurator` to false across the entire app.
 * - AskKlue-specific view-as: `?askKlueVisibilityGroupId=<id>`, read directly
 *   here. Does NOT flip `isCurator`, only narrows the AskKlue surface.
 *
 * Either one being set means the user is intentionally previewing a non-curator
 * view; both are mapped to the same FE/BE preview signal.
 */
export function useEffectivePreviewingVisibilityGroup(): number | null {
  const { visibilityGroup } = useAuth();
  const [queryParams] = useQueryParams(QueryParamConfig);

  if (visibilityGroup != null) {
    return visibilityGroup;
  }

  if (queryParams.askKlueVisibilityGroupId != null) {
    const parsed = parseInt(queryParams.askKlueVisibilityGroupId, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
}
